import React, { useState } from "react";
import { Modal, Pressable, ScrollView, StyleSheet, TextInput, TouchableOpacity, View } from "react-native";
import { CheckCircleIcon, MagnifyingGlassIcon, PaperPlaneTiltIcon, XIcon } from "@phosphor-icons/react";
import { CourseModality, CourseType, EducationLevel } from "@axio/graphql/generated/graphql";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomDropdown from "../../common/general/CustomDropDown/CustomDropdown";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";

export type CourseModernizerSetupUserOption = {
  id: string;
  label: string;
  subtitle?: string | null;
};

export type CourseModernizerSetupFormState = {
  intakeId: string;
  courseType: CourseType;
  name: string;
  courseCode: string;
  architectId: string;
  term: string;
  educationLevel: EducationLevel | "";
  yearLevel: string;
  credits: string;
  lengthInWeeks: string;
  modality: CourseModality;
};

type CourseModernizerSetupDrawerProps = {
  visible: boolean;
  courseSummary: {
    name: string;
    code: string | null;
    term: string | null;
    faculty: string | null;
  } | null;
  form: CourseModernizerSetupFormState;
  onFormChange: (nextForm: CourseModernizerSetupFormState) => void;
  userOptions: CourseModernizerSetupUserOption[];
  userSearchQuery: string;
  onUserSearchQueryChange: (value: string) => void;
  currentUserId?: string | null;
  loadingShell: boolean;
  saving: boolean;
  saveDisabled: boolean;
  validationMessage: string | null;
  onClose: () => void;
  onSave: () => void;
};

const EDUCATION_LEVEL_OPTIONS = [
  { label: "Undergraduate", value: EducationLevel.Undergraduate },
  { label: "Graduate", value: EducationLevel.Graduate },
  { label: "Professional", value: EducationLevel.Professional },
  { label: "K-12", value: EducationLevel.K12 },
];

const YEAR_LEVEL_OPTIONS = [
  { label: "Freshman", value: "Freshman" },
  { label: "Sophomore", value: "Sophomore" },
  { label: "Junior", value: "Junior" },
  { label: "Senior", value: "Senior" },
  { label: "Mixed", value: "Mixed" },
];

const MODALITY_OPTIONS = [
  {
    value: CourseModality.FaceToFace,
    label: "Face-to-face",
    description: "All sessions in person",
  },
  {
    value: CourseModality.OnlineSync,
    label: "Online synchronous",
    description: "Live virtual sessions on a schedule",
  },
  {
    value: CourseModality.OnlineAsync,
    label: "Online asynchronous",
    description: "Students progress on their own time",
  },
  {
    value: CourseModality.Hybrid,
    label: "Hybrid",
    description: "Mix of in-person and online",
  },
];

type OpenDropdown = "educationLevel" | "yearLevel" | null;

const getInitials = (label: string): string => {
  const initials = label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
  return initials || "U";
};

function LabeledInput({
  label,
  value,
  onChangeText,
  placeholder,
  disabled = false,
  keyboardType,
}: {
  label: string;
  value: string;
  onChangeText: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  keyboardType?: "default" | "numeric";
}) {
  return (
    <View style={styles.field}>
      <CustomText text={label} size="xs" weight="medium" style={styles.fieldLabel} useTranslationText={false} />
      <TextInput
        accessibilityLabel={label}
        editable={!disabled}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor="#667085"
        keyboardType={keyboardType}
        style={[styles.textInput, disabled && styles.textInputDisabled]}
      />
    </View>
  );
}

function CourseModernizerSetupDrawer({
  visible,
  courseSummary,
  form,
  onFormChange,
  userOptions,
  userSearchQuery,
  onUserSearchQueryChange,
  currentUserId = null,
  loadingShell,
  saving,
  saveDisabled,
  validationMessage,
  onClose,
  onSave,
}: CourseModernizerSetupDrawerProps) {
  const [openDropdown, setOpenDropdown] = useState<OpenDropdown>(null);
  const selectedArchitect = userOptions.find((option) => option.id === form.architectId) ?? null;
  const normalizedSearchQuery = userSearchQuery.trim().toLowerCase();
  const visibleArchitectOptions = selectedArchitect
    ? []
    : normalizedSearchQuery
      ? userOptions.filter((option) =>
          [option.label, option.subtitle ?? ""].some((value) => value.toLowerCase().includes(normalizedSearchQuery))
        )
      : [];
  const courseStateLabel = form.courseType === CourseType.Blueprint ? "Blueprint" : "Live";

  const setFormValue = <Key extends keyof CourseModernizerSetupFormState>(
    key: Key,
    value: CourseModernizerSetupFormState[Key]
  ) => {
    onFormChange({ ...form, [key]: value });
  };

  const selectArchitect = (userId: string) => {
    onFormChange({
      ...form,
      architectId: userId,
    });
  };

  const clearArchitect = () => {
    onFormChange({
      ...form,
      architectId: "",
    });
  };

  return (
    <Modal
      transparent={true}
      visible={visible}
      animationType="fade"
      onRequestClose={onClose}
      role="dialog"
      accessibilityRole="dialog"
      aria-modal="true"
      accessibilityViewIsModal={true}
      accessibilityLabel="Set up modernization">
      <View style={styles.overlay}>
        <Pressable accessibilityLabel="Close setup drawer backdrop" onPress={onClose} style={styles.backdrop} />
        <View style={styles.drawer}>
          <View style={styles.header}>
            <View style={styles.headerText}>
              <CustomText
                text="Set up modernization"
                size="m"
                textType="display"
                weight="bold"
                style={styles.title}
                useTranslationText={false}
              />
              <CustomText
                text="Pick an architect and confirm the course context. We'll email the architect an invitation to start intake."
                size="s"
                style={styles.subtitle}
                useTranslationText={false}
              />
            </View>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel="Close setup drawer"
              onPress={onClose}
              style={styles.closeButton}>
              <XIcon color="#667085" size={18} weight="bold" />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
            {courseSummary ? (
              <View style={styles.courseSummary}>
                <View style={styles.courseCodeBadge}>
                  <CustomText
                    text={courseSummary.code ?? "COURSE"}
                    size="xs"
                    weight="bold"
                    style={styles.courseCodeText}
                    useTranslationText={false}
                  />
                </View>
                <View style={styles.courseSummaryText}>
                  <CustomText
                    text={courseSummary.name}
                    size="s"
                    weight="bold"
                    style={styles.summaryTitle}
                    useTranslationText={false}
                  />
                  <CustomText
                    text={
                      [courseSummary.term, courseSummary.faculty].filter(Boolean).join(" · ") || "No source details"
                    }
                    size="xs"
                    style={styles.subtitle}
                    useTranslationText={false}
                  />
                </View>
                <View style={styles.courseStatePill}>
                  <CustomText
                    text={courseStateLabel}
                    size="xs"
                    weight="bold"
                    style={styles.courseStatePillText}
                    useTranslationText={false}
                  />
                </View>
              </View>
            ) : null}

            <View style={styles.section}>
              <CustomText text="Course architect" size="s" weight="bold" useTranslationText={false} />
              <CustomText
                text="Owns intake and the modernization run. Will receive an email invitation when you send."
                size="xs"
                style={styles.subtitle}
                useTranslationText={false}
              />
              <CustomSpacing type="vertical" size="s" />
              {selectedArchitect ? (
                <View style={styles.selectedArchitectCard}>
                  <View style={styles.userAvatar}>
                    <CustomText
                      text={getInitials(selectedArchitect.label)}
                      size="xs"
                      weight="bold"
                      style={styles.userAvatarText}
                      useTranslationText={false}
                    />
                  </View>
                  <View style={styles.selectedArchitectText}>
                    <CustomText text={selectedArchitect.label} size="s" weight="bold" useTranslationText={false} />
                    {selectedArchitect.subtitle ? (
                      <CustomText
                        text={selectedArchitect.subtitle}
                        size="xs"
                        style={styles.subtitle}
                        useTranslationText={false}
                      />
                    ) : null}
                  </View>
                  {currentUserId && selectedArchitect.id === currentUserId ? (
                    <View style={styles.rolePill}>
                      <CustomText
                        text="You"
                        size="xs"
                        weight="bold"
                        style={styles.rolePillText}
                        useTranslationText={false}
                      />
                    </View>
                  ) : null}
                  <View style={[styles.rolePill, styles.architectPill]}>
                    <CustomText
                      text="Architect"
                      size="xs"
                      weight="bold"
                      style={styles.architectPillText}
                      useTranslationText={false}
                    />
                  </View>
                  <TouchableOpacity
                    accessibilityRole="button"
                    accessibilityLabel="Remove selected architect"
                    onPress={clearArchitect}
                    style={styles.iconButton}>
                    <XIcon color="#98A2B3" size={16} weight="bold" />
                  </TouchableOpacity>
                </View>
              ) : (
                <>
                  <View style={styles.searchInputWrapper}>
                    <MagnifyingGlassIcon color="#667085" size={16} />
                    <TextInput
                      testID="modernizer-architect-search-input"
                      accessibilityLabel="Search course architect"
                      value={userSearchQuery}
                      onChangeText={onUserSearchQueryChange}
                      placeholder="Search by name or email"
                      placeholderTextColor="#667085"
                      style={styles.searchInput}
                    />
                  </View>
                  <View style={styles.userList}>
                    {visibleArchitectOptions.length ? (
                      visibleArchitectOptions.map((option) => (
                        <TouchableOpacity
                          key={option.id}
                          accessibilityRole="button"
                          accessibilityLabel={`Select course architect ${option.label}`}
                          onPress={() => selectArchitect(option.id)}
                          style={styles.userOption}>
                          <View style={styles.userAvatar}>
                            <CustomText
                              text={getInitials(option.label)}
                              size="xs"
                              weight="bold"
                              style={styles.userAvatarText}
                              useTranslationText={false}
                            />
                          </View>
                          <View style={styles.userOptionText}>
                            <CustomText text={option.label} size="s" weight="medium" useTranslationText={false} />
                            {option.subtitle ? (
                              <CustomText
                                text={option.subtitle}
                                size="xs"
                                style={styles.subtitle}
                                useTranslationText={false}
                              />
                            ) : null}
                          </View>
                        </TouchableOpacity>
                      ))
                    ) : userSearchQuery.trim() ? (
                      <CustomText
                        text="No matching users"
                        size="s"
                        style={styles.subtitle}
                        useTranslationText={false}
                      />
                    ) : null}
                  </View>
                </>
              )}
            </View>

            <View style={styles.divider} />

            <View style={styles.contextSection}>
              <CustomText
                text="* Required fields"
                size="xs"
                style={styles.requiredFieldsHint}
                useTranslationText={false}
              />
              <View style={styles.fieldGrid}>
                <View style={styles.field}>
                  <CustomText
                    text="Education level *"
                    size="xs"
                    weight="medium"
                    style={styles.fieldLabel}
                    useTranslationText={false}
                  />
                  <CustomDropdown
                    options={EDUCATION_LEVEL_OPTIONS}
                    value={form.educationLevel}
                    onChange={(value) => setFormValue("educationLevel", value as EducationLevel | "")}
                    placeholder="Select level"
                    accessibilityLabel="Education level"
                    open={openDropdown === "educationLevel"}
                    onOpenChange={(open) => setOpenDropdown(open ? "educationLevel" : null)}
                    fullWidth={true}
                  />
                </View>
                <View style={styles.field}>
                  <CustomText
                    text="Year level *"
                    size="xs"
                    weight="medium"
                    style={styles.fieldLabel}
                    useTranslationText={false}
                  />
                  <CustomDropdown
                    options={YEAR_LEVEL_OPTIONS}
                    value={form.yearLevel}
                    onChange={(value) => setFormValue("yearLevel", value)}
                    placeholder="Select year"
                    accessibilityLabel="Year level"
                    open={openDropdown === "yearLevel"}
                    onOpenChange={(open) => setOpenDropdown(open ? "yearLevel" : null)}
                    fullWidth={true}
                  />
                </View>
                <LabeledInput
                  label="Term *"
                  value={form.term}
                  onChangeText={(value) => setFormValue("term", value)}
                  placeholder="Enter term"
                />
                <LabeledInput
                  label="Credits *"
                  value={form.credits}
                  onChangeText={(value) => setFormValue("credits", value)}
                  placeholder="Enter credits"
                  keyboardType="numeric"
                />
                <LabeledInput
                  label="Course length *"
                  value={form.lengthInWeeks}
                  onChangeText={(value) => setFormValue("lengthInWeeks", value)}
                  placeholder="Enter weeks"
                  keyboardType="numeric"
                />
              </View>
            </View>

            <View style={styles.section}>
              <CustomText text="Modality" size="s" weight="bold" useTranslationText={false} />
              <View style={styles.modalityGrid}>
                {MODALITY_OPTIONS.map((option) => (
                  <TouchableOpacity
                    key={option.value}
                    accessibilityRole="radio"
                    role="radio"
                    aria-checked={form.modality === option.value}
                    accessibilityState={{ checked: form.modality === option.value }}
                    accessibilityLabel={option.label}
                    onPress={() => setFormValue("modality", option.value)}
                    style={[styles.modalityCard, form.modality === option.value && styles.modalityCardSelected]}>
                    {form.modality === option.value ? (
                      <View style={styles.modalityCheck}>
                        <CheckCircleIcon color="#3E68FE" size={16} weight="fill" />
                      </View>
                    ) : null}
                    <CustomText text={option.label} size="s" weight="bold" useTranslationText={false} />
                    <CustomText
                      text={option.description}
                      size="xs"
                      style={styles.subtitle}
                      useTranslationText={false}
                    />
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {validationMessage ? (
              <CustomText text={validationMessage} size="xs" style={styles.warningText} useTranslationText={false} />
            ) : null}
          </ScrollView>

          <View style={styles.footer}>
            <CustomButton
              text="Cancel"
              styleType="lightStroke"
              size="s"
              style={styles.footerButton}
              useTranslationText={false}
              disabled={saving || loadingShell}
              onPress={onClose}
            />
            <CustomButton
              text={saving ? "Sending..." : "Send invitation"}
              styleType="primary"
              size="s"
              style={styles.footerButtonPrimary}
              leftIcon={<PaperPlaneTiltIcon color="#FFFFFF" size={14} weight="bold" />}
              useTranslationText={false}
              disabled={saveDisabled || saving || loadingShell}
              onPress={onSave}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    flexDirection: "row",
    backgroundColor: "rgba(16, 24, 40, 0.28)",
  },
  backdrop: {
    flex: 1,
  },
  drawer: {
    width: "100%",
    maxWidth: 640,
    backgroundColor: "#FFFFFF",
    borderLeftWidth: 1,
    borderLeftColor: "#EAECF0",
  },
  header: {
    paddingHorizontal: 24,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
  },
  headerText: {
    flex: 1,
  },
  title: {
    color: "#101828",
    fontSize: 22,
    lineHeight: 28,
  },
  subtitle: {
    color: "#667085",
    lineHeight: 19,
  },
  closeButton: {
    width: 32,
    height: 32,
    alignItems: "center",
    justifyContent: "center",
  },
  body: {
    flex: 1,
  },
  bodyContent: {
    paddingHorizontal: 24,
    paddingVertical: 20,
    gap: 18,
  },
  courseSummary: {
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 10,
    padding: 14,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  courseCodeBadge: {
    backgroundColor: "#DDE5FF",
    borderRadius: 8,
    minWidth: 48,
    minHeight: 40,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  courseCodeText: {
    color: "#0A2EA8",
  },
  courseSummaryText: {
    flex: 1,
  },
  summaryTitle: {
    color: "#101828",
  },
  courseStatePill: {
    borderRadius: 999,
    backgroundColor: "#F2F4F7",
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  courseStatePillText: {
    color: "#344054",
  },
  section: {
    gap: 8,
  },
  divider: {
    height: 1,
    backgroundColor: "#EAECF0",
    marginVertical: 8,
  },
  contextSection: {
    gap: 12,
  },
  fieldGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  requiredFieldsHint: {
    color: "#667085",
  },
  field: {
    flexGrow: 1,
    flexBasis: "47%",
    minWidth: 220,
    zIndex: 1,
  },
  fieldLabel: {
    color: "#344054",
    marginBottom: 6,
  },
  textInput: {
    borderWidth: 1,
    borderColor: "#D0D5DD",
    borderRadius: 8,
    color: "#101828",
    fontSize: 14,
    lineHeight: 20,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minHeight: 44,
    outlineColor: "#84ADFF",
  },
  textInputDisabled: {
    backgroundColor: "#F2F4F7",
    color: "#98A2B3",
  },
  searchInputWrapper: {
    borderWidth: 1,
    borderColor: "#D0D5DD",
    borderRadius: 8,
    minHeight: 44,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  searchInput: {
    flex: 1,
    color: "#101828",
    fontSize: 14,
    borderWidth: 0,
    outlineStyle: "none",
    outlineWidth: 0,
    boxShadow: "none",
  },
  selectedArchitectCard: {
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 10,
    padding: 14,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  selectedArchitectText: {
    flex: 1,
  },
  rolePill: {
    borderRadius: 999,
    backgroundColor: "#F2F4F7",
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  rolePillText: {
    color: "#344054",
  },
  architectPill: {
    backgroundColor: "#DDE5FF",
  },
  architectPillText: {
    color: "#102A83",
  },
  iconButton: {
    width: 28,
    height: 28,
    alignItems: "center",
    justifyContent: "center",
  },
  userList: {
    gap: 8,
    marginTop: 8,
  },
  userOption: {
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 8,
    padding: 10,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  userAvatar: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#DDE5FF",
  },
  userAvatarText: {
    color: "#0A2EA8",
  },
  userOptionText: {
    flex: 1,
  },
  modalityGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  modalityCard: {
    flexGrow: 1,
    flexBasis: "47%",
    minWidth: 220,
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 10,
    paddingVertical: 14,
    paddingLeft: 14,
    paddingRight: 36,
    gap: 4,
    position: "relative",
  },
  modalityCardSelected: {
    borderColor: "#B2CCFF",
    backgroundColor: "#EEF4FF",
  },
  modalityCheck: {
    position: "absolute",
    top: 12,
    right: 12,
  },
  warningText: {
    color: "#B42318",
  },
  footer: {
    borderTopWidth: 1,
    borderTopColor: "#EAECF0",
    paddingHorizontal: 24,
    paddingVertical: 14,
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
  },
  footerButton: {
    minWidth: 92,
  },
  footerButtonPrimary: {
    minWidth: 132,
  },
});

export default CourseModernizerSetupDrawer;
